import {lerp, smoothNudge} from "./lerp";

export class Vec2 {
  x: number;
  y: number;

  constructor(x: number, y: number) {
    this.x = x;
    this.y = y;
  }

  static zero(): Vec2 {
    return Vec2.splat(0.0);
  }

  static fromTuple(tuple: [number, number]): Vec2 {
    return new Vec2(tuple[0], tuple[1]);
  }

  static splat(value: number): Vec2 {
    return new Vec2(value, value);
  }

  key(): string {
    return `${this.x},${this.y}`;
  }

  tuple(): [number, number] {
    return [this.x, this.y];
  }

  clone(): Vec2 {
    return new Vec2(this.x, this.y);
  }

  equals(other: Vec2): boolean {
    return this.x === other.x && this.y === other.y;
  }

  negate(): Vec2 {
    return new Vec2(-this.x, -this.y);
  }

  add(other: Vec2): Vec2 {
    return new Vec2(this.x + other.x, this.y + other.y);
  }

  sub(other: Vec2): Vec2 {
    return new Vec2(this.x - other.x, this.y - other.y);
  }

  fract(): Vec2 {
    return new Vec2(this.x - Math.trunc(this.x), this.y - Math.trunc(this.y));
  }

  dot(other: Vec2): number {
    return this.x * other.x + this.y * other.y;
  }

  addScalar(scalar: number): Vec2 {
    return new Vec2(this.x + scalar, this.y + scalar);
  }

  subScalar(scalar: number): Vec2 {
    return new Vec2(this.x - scalar, this.y - scalar);
  }

  mulScalar(scalar: number): Vec2 {
    return new Vec2(this.x * scalar, this.y * scalar);
  }

  divScalar(scalar: number): Vec2 {
    return new Vec2(this.x / scalar, this.y / scalar);
  }

  floor(): Vec2 {
    return new Vec2(Math.floor(this.x), Math.floor(this.y));
  }

  trunc(): Vec2 {
    return new Vec2(Math.trunc(this.x), Math.trunc(this.y));
  }

  ceil(): Vec2 {
    return new Vec2(Math.ceil(this.x), Math.ceil(this.y));
  }

  round(): Vec2 {
    return new Vec2(Math.round(this.x), Math.round(this.y));
  }

  lengthSquared(): number {
    return this.x * this.x + this.y * this.y;
  }

  length(): number {
    return Math.sqrt(this.lengthSquared());
  }

  lerp(other: Vec2, t: number): Vec2 {
    return new Vec2(lerp(this.x, other.x, t), lerp(this.y, other.y, t));
  }

  smoothNudge(other: Vec2, decayRate: number, delta: number, clampDistance?: number): Vec2 {
    return new Vec2(
      smoothNudge(this.x, other.x, decayRate, delta, clampDistance),
      smoothNudge(this.y, other.y, decayRate, delta, clampDistance),
    );
  }
}
